import { AccordionContent as AccordionContentRadix } from '@radix-ui/react-accordion';
import cn from 'classnames';
import { forwardRef, useEffect, useRef } from 'react';
import { mergeRefs } from 'react-merge-refs';

import { useAccordionGroupContext } from '../accordion-group/accordion-group-context';

import type { ForwardedRef, ReactElement } from 'react';

import type { AccordionContentProps } from './accordion-content.types';

import './accordion-content.scss';

/**
 * The `AccordionContent` component contains the content that is revealed or hidden when the `AccordionHeader`
 * is clicked. It is typically used to display detailed information related to the header.
 *
 * ### Usage
 * Place the `AccordionContent` component inside an `Accordion` component to define the content of the accordion.
 *
 * ```tsx
 * import { AccordionGroup, Accordion, AccordionHeader, AccordionContent } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   return (
 *     <AccordionGroup>
 *       <Accordion value="banana-facts">
 *         <AccordionHeader>Banana facts</AccordionHeader>
 *         <AccordionContent>
 *           Bananas are a great source of potassium and can help replenish electrolytes after exercise.
 *         </AccordionContent>
 *       </Accordion>
 *       <Accordion>...</Accordion>
 *       <Accordion>...</Accordion>
 *     </AccordionGroup>
 *   );
 * }
 * ```
 */
const AccordionContent = forwardRef(
  (props: AccordionContentProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): ReactElement => {
    const {
      children,
      className,
      keepMounted = false,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const accordionGroupContextValue = useAccordionGroupContext();
    if (accordionGroupContextValue === null) {
      throw new Error('No AccordionContext provided');
    }

    const contentRef = useRef<HTMLDivElement | null>(null);
    const innerContentRef = useRef<HTMLDivElement | null>(null);

    const handleRef = mergeRefs([contentRef, forwardedRef]);

    // Radix Accordion doesn't update height when forceMount.
    // We need to manualy add a resize observer on children of the AccordionContent component
    // to update the css variable height of the content.
    useEffect(() => {
      const contentElement = contentRef.current;
      const innerContentElement = innerContentRef.current;

      if (!contentElement || !innerContentElement || !keepMounted) {
        return;
      }

      const resizeObserver = new ResizeObserver(() => {
        const currentHeight = innerContentElement.clientHeight;
        contentElement.style.cssText += `--rbui-internal-accordion-content-height: ${currentHeight}px;`;
      });

      resizeObserver.observe(innerContentElement);

      return () => resizeObserver.disconnect();
    }, [keepMounted]);

    const { styling } = accordionGroupContextValue;

    const classes = cn(
      'rbui-accordion-content__inner',
      {
        'rbui-accordion-content__inner--styled': styling === 'styled',
      },
      className,
    );

    return (
      <AccordionContentRadix
        className="rbui-accordion-content"
        data-forcemounted={!!keepMounted}
        forceMount={keepMounted ? true : undefined}
        ref={handleRef}
        {...rest}
      >
        <div className={classes} ref={innerContentRef}>
          {children}
        </div>
      </AccordionContentRadix>
    );
  },
);

AccordionContent.displayName = 'AccordionContent';

export default AccordionContent;
